import type {
  GuardarMargenesVesBody,
  GuardarMargenesVesResponse,
  MargenesVesResponse,
  MotorTasasVes,
  ParFranja,
  RangoMargenVes,
  SpotCruzadoVes,
  TramoMargenVes,
} from '../../services/main-rates.service';
import { esTextoDeProtocolo, pasaTopeVesClp, statusDe } from './franjas-editor.rules';

/**
 * Motor de márgenes CLP↔VES (03/11/2026). Reglas puras de la sección
 * «Márgenes CLP ↔ VES» del modal «Actualizar Parámetros», del editor de
 * franjas en MARGENES y de las píldoras del panel. Sin Angular ni HTTP.
 *
 * El reparto por franja y la tasa de cada una son ESPEJO de
 * `src/modules/admin/rates/franjas/margenes.calculo.ts` del repo de Nest.
 * El back es la autoridad: lo que se graba y se cobra lo calcula él. Acá solo
 * se usa para la vista previa de la confirmación (antes de guardar).
 *
 * Unidades (las de siempre): las tasas se guardan como VES por 1 CLP en las
 * dos rutas.
 *   CLP→VES: el cliente recibe CLP × tasa. La tasa de la ruta es CLP por 1 VES.
 *   VES→CLP: el cliente recibe VES ÷ tasa. La tasa de la ruta es VES por 1 CLP.
 * Spot cruzado: `vesClp` = VES/USDT ÷ USD/CLP (VES por 1 CLP) y
 * `clpVes` = USD/CLP ÷ VES/USDT (CLP por 1 VES).
 */

// ───────────────────────── Constantes y textos ─────────────────────────

/** Tope de cada margen, en % (el back lo manda en `limiteMaximoPct`). */
export const LIMITE_MARGEN_PCT = 20;
/** Decimales máximos de un margen (igual que el back). */
export const DECIMALES_MARGEN = 4;
/** Motivo del PUT: opcional, hasta 255 caracteres. */
export const MOTIVO_MARGENES_MAX = 255;

export const TEXTO_REPARTO_AUTOMATICO =
  'Los márgenes se reparten solos entre el mínimo y el máximo de Actualizar Parámetros.';

export const TEXTO_LEGADO_NO_AFECTA =
  'Con el motor Márgenes, la comisión CLP - VES, el descuento fiat y la comisión VES - CLP no afectan las tasas CLP ↔ VES.';

export const TEXTO_MARGENES_NO_DISPONIBLE = 'El motor de márgenes no está disponible en este momento.';

export const TEXTO_SIN_SPOT =
  'Sin spot cruzado: falta el USD/CLP o el VES/USDT del operador activo. Las tasas no se pueden calcular.';

/** VES→CLP: aviso del 5 % (ver `pasaTopeVesClp`), con lo que se hace en MARGENES. */
export const TEXTO_PASA_TOPE_MARGENES =
  'Pasa el 5 %: algunos retiros serían rechazados. Baja el margen máximo en Actualizar Parámetros.';

/** «Revisar cambios» releyó lo guardado y otro operador lo había cambiado. */
export const TEXTO_OTRO_OPERADOR =
  'Otro operador cambió los márgenes mientras editabas: revisa antes de confirmar.';

/** 409 del PUT sin mensaje propio (el back manda este mismo). */
export const TEXTO_CONFLICTO_MARGENES =
  'Otro operador cambió los márgenes o el motor. Recarga y vuelve a intentar.';

/** Tras el 409 el modal ya releyó lo guardado: lo escrito sigue en la tabla. */
export const TEXTO_TRAS_CONFLICTO =
  'Ya se cargó lo que está guardado ahora; tus cambios siguen en la tabla para que los revises.';

/** El PUT falló (5xx, sin conexión) y la relectura muestra que no se guardó nada. */
export const TEXTO_NO_SE_GUARDO = 'Los márgenes no se guardaron: lo guardado sigue igual.';

/** El PUT falló y tampoco se pudo releer: no se sabe si se guardó. */
export const TEXTO_GUARDADO_INCIERTO =
  'No se pudo confirmar si los márgenes se guardaron. Vuelve a confirmar en unos segundos: si ya estaban guardados, se avisará.';

/** El PUT falló o chocó, pero lo guardado ya es justo lo que se pidió. */
export const TEXTO_YA_GUARDADO = 'Márgenes guardados: lo guardado ya tiene estos valores.';

/** …y con MARGENES no se sabe si las tasas nuevas alcanzaron a grabarse. */
export const TEXTO_APLICAR_INCIERTO =
  'No se pudo confirmar si las tasas nuevas ya se grabaron: si no cambiaron, usa «Reintentar aplicar».';

/** «Revisar cambios» no pudo releer lo guardado. */
export const TEXTO_NO_SE_PUDO_REVISAR = 'No se pudo leer lo guardado para revisar los cambios. Intenta de nuevo.';

/** Los márgenes guardados con LEGADO están en la base, pero no se cobran. */
export const TEXTO_ANTES_NO_RIGE = 'guardado, no rige con LEGADO';

/** MARGENES sin los márgenes del par en la BD: el editor no deja guardar. */
export function textoFaltanMargenes(par: ParFranja): string {
  const ruta = par === 'CLP-VES' ? 'CLP → VES' : 'VES → CLP';
  return `Faltan los márgenes mínimo y máximo de ${ruta}: complétalos en Actualizar Parámetros. Mientras tanto no se puede guardar.`;
}

// ───────────────────────── Rutas y motor ─────────────────────────

/** Clave de la ruta en el contrato (`margenes.clpVes`, `tramos.vesClp`…). */
export type RutaVes = 'clpVes' | 'vesClp';
export type CampoMargen = 'minimo' | 'maximo';

export interface InfoRuta {
  ruta: RutaVes;
  par: ParFranja;
  etiqueta: string;
  /** Quién entrega qué, para la tabla del modal. */
  entrega: string;
}

export const RUTAS_VES: readonly InfoRuta[] = [
  { ruta: 'clpVes', par: 'CLP-VES', etiqueta: 'CLP → VES', entrega: 'el cliente entrega CLP' },
  { ruta: 'vesClp', par: 'VES-CLP', etiqueta: 'VES → CLP', entrega: 'el cliente entrega VES' },
];

export const CAMPOS_MARGEN: readonly CampoMargen[] = ['minimo', 'maximo'];

export const MOTORES_VES: readonly { valor: MotorTasasVes; etiqueta: string }[] = [
  { valor: 'LEGADO', etiqueta: 'Legado (comisión y descuentos)' },
  { valor: 'MARGENES', etiqueta: 'Márgenes (spot cruzado)' },
];

/** El motor si es uno conocido; null si no (back viejo, cuerpo vacío). */
export function motorDe(v: unknown): MotorTasasVes | null {
  return v === 'LEGADO' || v === 'MARGENES' ? v : null;
}

/** «Legado (comisión y descuentos)» / «Márgenes (spot cruzado)»; «—» si no hay motor. */
export function etiquetaMotor(m: MotorTasasVes | null | undefined): string {
  return MOTORES_VES.find((x) => x.valor === m)?.etiqueta ?? '—';
}

/** Rótulo corto para las píldoras: «Márgenes» / «Legado». */
export function etiquetaMotorCorta(m: MotorTasasVes | null | undefined): string {
  return m === 'MARGENES' ? 'Márgenes' : m === 'LEGADO' ? 'Legado' : '—';
}

export function rutaDePar(par: ParFranja): RutaVes {
  return par === 'CLP-VES' ? 'clpVes' : 'vesClp';
}

/** Unidad del spot cruzado y de la tasa de la ruta. */
export function unidadRuta(par: ParFranja): string {
  return par === 'CLP-VES' ? 'CLP por 1 VES' : 'VES por 1 CLP';
}

// ───────────────────────── Reparto y tasa (espejo del back) ─────────────────────────

const r4 = (n: number): number => Math.round(n * 1e4) / 1e4;

function numOrNull(v: unknown): number | null {
  const n = Number(v);
  return v != null && v !== '' && typeof v !== 'boolean' && Number.isFinite(n) ? n : null;
}

/**
 * Margen de cada franja activa, ordenadas por «desde» de menor a mayor: la
 * primera (montos chicos) lleva el máximo, la última el mínimo y las del medio
 * se reparten en línea recta:
 *
 *   m_i = máximo − (máximo − mínimo) × i / (n − 1)
 *
 * Con una sola franja, el máximo. Ej.: 3 franjas entre 1 y 3 → 3, 2, 1;
 * 4 franjas → 3; 2,33; 1,67; 1. La última lleva el mínimo EXACTO, como el back
 * (`margenesPorTramo`): la resta en coma flotante dejaría 0,6000000000000001.
 */
export function margenesPorFranja(n: number, minimo: number, maximo: number): number[] {
  const k = Math.floor(Number(n));
  if (!(k >= 1)) return [];
  if (k === 1) return [maximo];
  return Array.from({ length: k }, (_, i) =>
    i === k - 1 ? minimo : maximo - ((maximo - minimo) * i) / (k - 1),
  );
}

/**
 * Lo que gana Aguacate sobre el spot (el mid) con un margen `m` (%) en
 * VES→CLP: la tasa es spot × (1 + m/100) y el cliente recibe VES ÷ tasa, así
 * que la ganancia es 1 − 1/(1 + m/100) = m / (1 + m/100). Es la misma cuenta
 * de «Tu ganancia %» del editor de franjas (`gananciaVsMercado`).
 */
export function margenSobreMid(m: number | null | undefined): number | null {
  const x = Number(m);
  if (m == null || !Number.isFinite(x) || !(1 + x / 100 > 0)) return null;
  return x / (1 + x / 100);
}

/**
 * VES→CLP: el margen de la franja llega al 5 % sobre el mid del aviso de
 * siempre (`pasaTopeVesClp`): algunos retiros serían rechazados. CLP→VES no
 * tiene ese tope.
 */
export function pasaTopeMargen(par: ParFranja, m: number | null | undefined): boolean {
  return pasaTopeVesClp(par, par === 'VES-CLP' ? margenSobreMid(m) : null);
}

export interface TasaConMargen {
  /** CLP-VES: CLP por 1 VES; VES-CLP: VES por 1 CLP. Sin redondear. */
  tasaRuta: number;
  /** VES por 1 CLP a 4 decimales: lo que se graba. */
  tasaClient: number;
}

/**
 * Tasa de una franja con su margen, sobre el spot cruzado de la ruta:
 *   CLP-VES: tasaRuta = spot.clpVes × (1 + m/100)   (CLP por 1 VES)
 *            tasaClient = r4(1 ÷ tasaRuta)
 *   VES-CLP: tasaRuta = spot.vesClp × (1 + m/100)   (VES por 1 CLP)
 *            tasaClient = r4(tasaRuta)
 * `spot` es el de la ruta (ver `spotDeRuta`). Null si falta algo.
 */
export function tasaConMargen(
  par: ParFranja,
  spot: number | null | undefined,
  margenPct: number | null | undefined,
): TasaConMargen | null {
  const s = Number(spot);
  const m = Number(margenPct);
  if (spot == null || margenPct == null || !(s > 0) || !Number.isFinite(s) || !Number.isFinite(m)) return null;
  const tasaRuta = s * (1 + m / 100);
  if (!(tasaRuta > 0)) return null;
  return { tasaRuta, tasaClient: par === 'CLP-VES' ? r4(1 / tasaRuta) : r4(tasaRuta) };
}

/** El spot cruzado que usa cada ruta: CLP-VES el `clpVes`, VES-CLP el `vesClp`. */
export function spotDeRuta(par: ParFranja, spot: SpotCruzadoVes | null | undefined): number | null {
  const v = Number(par === 'CLP-VES' ? spot?.clpVes : spot?.vesClp);
  return spot && v > 0 && Number.isFinite(v) ? v : null;
}

/** Lo mínimo de una franja para repartir: su rango y, si lo trae el GET, lo que tiene hoy. */
export interface FranjaBase {
  clave?: string;
  letra?: string;
  desde: number;
  hasta: number;
  orden?: number;
  margenAplicado?: number | null;
  tasaRuta?: number | null;
  tasaClient?: number | null;
}

export interface FilaReparto {
  clave: string;
  letra: string;
  desde: number;
  hasta: number;
  /** Lo que tiene hoy (null si no se sabe). */
  margenAntes: number | null;
  tasaClientAntes: number | null;
  /** Con los márgenes nuevos. */
  margen: number | null;
  tasaRuta: number | null;
  tasaClient: number | null;
  /** VES→CLP: el margen pasa el 5 % sobre el mid (ver `pasaTopeMargen`). */
  pasaTope: boolean;
}

function letraDe(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i) : String(i + 1);
}

/** Por «desde» de menor a mayor (y `orden` si empatan), como el back. Sin rangos inválidos. */
export function ordenarFranjas<T extends FranjaBase>(franjas: readonly T[] | null | undefined): T[] {
  return (franjas || [])
    .filter((f) => f && Number.isFinite(Number(f.desde)) && Number.isFinite(Number(f.hasta)))
    .slice()
    .sort((a, b) => Number(a.desde) - Number(b.desde) || Number(a.orden ?? 0) - Number(b.orden ?? 0));
}

/**
 * Vista previa del reparto con unos márgenes sobre las mismas franjas. Sin
 * spot cruzado trae los márgenes pero no las tasas. Vacía si no hay franjas o
 * el rango no sirve.
 */
export function repartoConMargenes(
  par: ParFranja,
  franjas: readonly FranjaBase[] | null | undefined,
  rango: { minimo: number | null; maximo: number | null } | null | undefined,
  spot: number | null | undefined,
): FilaReparto[] {
  const lista = ordenarFranjas(franjas);
  const min = numOrNull(rango?.minimo);
  const max = numOrNull(rango?.maximo);
  if (!lista.length || min == null || max == null) return [];
  const margenes = margenesPorFranja(lista.length, min, max);
  return lista.map((f, i) => {
    const t = tasaConMargen(par, spot, margenes[i]);
    return {
      clave: f.clave || `${f.desde}-${f.hasta}`,
      letra: f.letra || letraDe(i),
      desde: Number(f.desde),
      hasta: Number(f.hasta),
      margenAntes: numOrNull(f.margenAplicado),
      tasaClientAntes: numOrNull(f.tasaClient),
      margen: margenes[i],
      tasaRuta: t?.tasaRuta ?? null,
      tasaClient: t?.tasaClient ?? null,
      pasaTope: pasaTopeMargen(par, margenes[i]),
    };
  });
}

/** El reparto guardado tal como lo manda el GET (para verlo sin editar). */
export function repartoGuardado(par: ParFranja, franjas: readonly FranjaBase[] | null | undefined): FilaReparto[] {
  return ordenarFranjas(franjas).map((f, i) => {
    const margen = numOrNull(f.margenAplicado);
    return {
      clave: f.clave || `${f.desde}-${f.hasta}`,
      letra: f.letra || letraDe(i),
      desde: Number(f.desde),
      hasta: Number(f.hasta),
      margenAntes: null,
      tasaClientAntes: null,
      margen,
      tasaRuta: numOrNull(f.tasaRuta),
      tasaClient: numOrNull(f.tasaClient),
      pasaTope: pasaTopeMargen(par, margen),
    };
  });
}

/**
 * Franjas activas de un par desde `rates/franjas/estado`: el respaldo para la
 * vista previa cuando el GET de márgenes no trae el reparto de una ruta (por
 * ejemplo, porque todavía no tiene márgenes guardados).
 */
export function franjasActivasDeEstado(estado: any, par: ParFranja): FranjaBase[] {
  const p = Array.isArray(estado?.pares) ? estado.pares.find((x: any) => x?.par === par) : null;
  const activas = ordenarFranjas<FranjaBase>(
    (Array.isArray(p?.franjas) ? p.franjas : [])
      .filter((f: any) => f && f.activo)
      .map((f: any) => ({ clave: f.clave, desde: Number(f.desde), hasta: Number(f.hasta), orden: Number(f.orden) })),
  );
  return activas.map((f, i) => ({ ...f, letra: letraDe(i) }));
}

// ───────────────────────── Validación (espejo del back) ─────────────────────────

/** Decimales con que JS escribe el número: «3.575» → 3; «1e-7» → 7. */
export function decimalesDe(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const [mantisa, exp] = String(Math.abs(n)).toLowerCase().split('e');
  const dec = (mantisa.split('.')[1] || '').length;
  return Math.max(0, dec - (exp ? Number(exp) : 0));
}

/**
 * Error de un margen suelto; null si sirve. Igual que el back: número finito,
 * de 0 al tope (20 %) y hasta 4 decimales.
 */
export function errorMargen(valor: unknown, limite = LIMITE_MARGEN_PCT): string | null {
  if (valor == null || valor === '') return 'Escribe un porcentaje.';
  const n = Number(valor);
  if (typeof valor === 'boolean' || !Number.isFinite(n)) return 'Tiene que ser un número.';
  if (n < 0) return 'No puede ser negativo.';
  if (n > limite) return `No puede pasar de ${formatoMargen(limite)}.`;
  if (decimalesDe(n) > DECIMALES_MARGEN) return `Hasta ${DECIMALES_MARGEN} decimales.`;
  return null;
}

export interface BorradorRuta {
  minimo: number | null;
  maximo: number | null;
}

export interface ErroresRuta {
  minimo: string | null;
  maximo: string | null;
  /** El mínimo quedó sobre el máximo. */
  rango: string | null;
}

/** Errores de una ruta: cada campo y el cruce (0 ≤ mínimo ≤ máximo ≤ tope). */
export function erroresRuta(r: BorradorRuta | null | undefined, limite = LIMITE_MARGEN_PCT): ErroresRuta {
  const minimo = errorMargen(r?.minimo, limite);
  const maximo = errorMargen(r?.maximo, limite);
  const rango =
    !minimo && !maximo && Number(r?.minimo) > Number(r?.maximo)
      ? 'El mínimo no puede ser mayor que el máximo.'
      : null;
  return { minimo, maximo, rango };
}

// ───────────────────────── Borrador del modal ─────────────────────────

export interface BorradorMargenes {
  motor: MotorTasasVes;
  clpVes: BorradorRuta;
  vesClp: BorradorRuta;
}

/** Borrador desde el GET: márgenes faltantes quedan vacíos (el operador los completa). */
export function borradorMargenesDesde(r: MargenesVesResponse | null | undefined): BorradorMargenes {
  const ruta = (x: RangoMargenVes | null | undefined): BorradorRuta => ({
    minimo: numOrNull(x?.minimo),
    maximo: numOrNull(x?.maximo),
  });
  return {
    motor: motorDe(r?.motor) ?? 'LEGADO',
    clpVes: ruta(r?.margenes?.clpVes),
    vesClp: ruta(r?.margenes?.vesClp),
  };
}

export function clonarBorradorMargenes(b: BorradorMargenes): BorradorMargenes {
  return { motor: b.motor, clpVes: { ...b.clpVes }, vesClp: { ...b.vesClp } };
}

/** Todos los errores, con la ruta y el campo: «CLP → VES · mínimo: No puede ser negativo.» */
export function erroresMargenes(b: BorradorMargenes | null | undefined, limite = LIMITE_MARGEN_PCT): string[] {
  if (!b) return [];
  const salida: string[] = [];
  RUTAS_VES.forEach((r) => {
    const e = erroresRuta(b[r.ruta], limite);
    if (e.minimo) salida.push(`${r.etiqueta} · mínimo: ${e.minimo}`);
    if (e.maximo) salida.push(`${r.etiqueta} · máximo: ${e.maximo}`);
    if (e.rango) salida.push(`${r.etiqueta}: ${e.rango}`);
  });
  return salida;
}

function mismoNumero(a: number | null, b: number | null): boolean {
  return a == null || b == null ? a == null && b == null : Number(a) === Number(b);
}

export function hayCambiosMargenes(b: BorradorMargenes | null | undefined, inicial: BorradorMargenes | null | undefined): boolean {
  if (!b || !inicial) return false;
  if (b.motor !== inicial.motor) return true;
  return RUTAS_VES.some((r) =>
    CAMPOS_MARGEN.some((c) => !mismoNumero(b[r.ruta][c], inicial[r.ruta][c])),
  );
}

export function motivoMargenesValido(motivo: string | null | undefined): boolean {
  return (motivo || '').trim().length <= MOTIVO_MARGENES_MAX;
}

/**
 * Cuerpo del PUT: siempre los 4 márgenes; `motor` solo si cambió (sin él, el
 * back conserva el actual, y así no se pisa un cambio de motor de otro
 * operador); `motivo` solo si se escribió; `version` (la del GET) solo si el
 * back la mandó: con ella, si otro operador guardó antes, 409 y no se guarda
 * nada. Null si el borrador no valida.
 */
export function cuerpoGuardarMargenes(
  b: BorradorMargenes,
  inicial: BorradorMargenes | null | undefined,
  motivo?: string | null,
  limite = LIMITE_MARGEN_PCT,
  version?: string | null,
): GuardarMargenesVesBody | null {
  if (erroresMargenes(b, limite).length || !motivoMargenesValido(motivo)) return null;
  const ruta = (r: BorradorRuta): RangoMargenVes => ({ minimo: Number(r.minimo), maximo: Number(r.maximo) });
  const cuerpo: GuardarMargenesVesBody = { clpVes: ruta(b.clpVes), vesClp: ruta(b.vesClp) };
  if (!inicial || b.motor !== inicial.motor) cuerpo.motor = b.motor;
  const m = (motivo || '').trim();
  if (m) cuerpo.motivo = m;
  if (typeof version === 'string' && version) cuerpo.version = version;
  return cuerpo;
}

/** El mismo cuerpo con otra versión (la de ahora): lo usa «Reintentar aplicar». */
export function conVersionMargenes(cuerpo: GuardarMargenesVesBody, version: string | null | undefined): GuardarMargenesVesBody {
  const copia: GuardarMargenesVesBody = { ...cuerpo };
  delete copia.version;
  if (typeof version === 'string' && version) copia.version = version;
  return copia;
}

/**
 * Lo guardado cambió mientras se editaba (otro operador): el borrador pasa a
 * apoyarse en lo guardado nuevo. Lo que este operador cambió (frente a lo que
 * había cargado) queda como lo escribió; lo que no tocó toma lo guardado
 * nuevo. Así no se pisa sin querer el cambio del otro.
 */
export function rebasarBorrador(
  borrador: BorradorMargenes,
  cargado: BorradorMargenes,
  guardadoNuevo: BorradorMargenes,
): BorradorMargenes {
  const campo = (r: RutaVes, c: CampoMargen): number | null =>
    mismoNumero(borrador[r][c], cargado[r][c]) ? guardadoNuevo[r][c] : borrador[r][c];
  const ruta = (r: RutaVes): BorradorRuta => ({ minimo: campo(r, 'minimo'), maximo: campo(r, 'maximo') });
  return {
    motor: borrador.motor !== cargado.motor ? borrador.motor : guardadoNuevo.motor,
    clpVes: ruta('clpVes'),
    vesClp: ruta('vesClp'),
  };
}

// ───────────────────────── Confirmación: antes → después ─────────────────────────

export interface CeldaCambio {
  antes: string;
  despues: string;
  cambia: boolean;
}

export interface FilaCambioRuta {
  ruta: RutaVes;
  par: ParFranja;
  etiqueta: string;
  minimo: CeldaCambio;
  maximo: CeldaCambio;
}

export interface ResumenCambiosMargenes {
  motor: CeldaCambio;
  rutas: FilaCambioRuta[];
  /** Cuántos valores cambian (motor incluido). */
  total: number;
}

/** Lo que muestra el paso de confirmación: los 4 márgenes y el motor, antes → después. */
export function resumenCambiosMargenes(inicial: BorradorMargenes, nuevo: BorradorMargenes): ResumenCambiosMargenes {
  const celda = (a: number | null, d: number | null): CeldaCambio => ({
    antes: formatoMargen(a),
    despues: formatoMargen(d),
    cambia: !mismoNumero(a, d),
  });
  const motor: CeldaCambio = {
    antes: etiquetaMotor(inicial.motor),
    despues: etiquetaMotor(nuevo.motor),
    cambia: inicial.motor !== nuevo.motor,
  };
  const rutas = RUTAS_VES.map((r) => ({
    ruta: r.ruta,
    par: r.par,
    etiqueta: r.etiqueta,
    minimo: celda(inicial[r.ruta].minimo, nuevo[r.ruta].minimo),
    maximo: celda(inicial[r.ruta].maximo, nuevo[r.ruta].maximo),
  }));
  const total =
    (motor.cambia ? 1 : 0) +
    rutas.reduce((s, r) => s + (r.minimo.cambia ? 1 : 0) + (r.maximo.cambia ? 1 : 0), 0);
  return { motor, rutas, total };
}

/**
 * Rutas cuyas tasas no se grabaron con el PUT, cuando el back intentó
 * grabarlas: con MARGENES siempre, y con LEGADO solo si el motor cambió (si
 * no, `aplicado` viene en false porque no había nada que grabar).
 */
export function rutasSinAplicar(
  r: GuardarMargenesVesResponse | null | undefined,
  motorAntes: MotorTasasVes | null | undefined,
): string[] {
  const motor = motorDe(r?.motor);
  const aplicado = r?.aplicado;
  if (!motor || !aplicado || (motor !== 'MARGENES' && motor === motorAntes)) return [];
  return RUTAS_VES.filter((x) => aplicado[x.ruta] === false).map((x) => x.etiqueta);
}

/**
 * Textos del resultado del PUT. Si el back manda `mensaje`, ese (ya dice qué
 * se grabó y qué no, y por qué): el front no le agrega nada. Sin mensaje (back
 * viejo o vacío), uno propio con las rutas que no se grabaron.
 */
export function textosGuardado(
  r: GuardarMargenesVesResponse | null | undefined,
  motorAntes?: MotorTasasVes | null,
): string[] {
  const mensaje = typeof r?.mensaje === 'string' && r.mensaje.trim() ? r.mensaje.trim() : null;
  if (mensaje) return [mensaje];
  const pendientes = rutasSinAplicar(r, motorAntes);
  if (!pendientes.length) return ['Márgenes guardados.'];
  return [
    `Márgenes guardados, pero las tasas de ${pendientes.join(' y ')} no se grabaron todavía. ` +
      (motorDe(r?.motor) === 'MARGENES' ? 'Usa «Reintentar aplicar» en unos segundos.' : 'Se grabarán con la próxima actualización.'),
  ];
}

/** Resultado del guardado (o de su error), a la vista en la sección «Márgenes CLP ↔ VES». */
export interface ResultadoMargenes {
  /** exito = verde; aviso = guardado, pero algo no se grabó o hay advertencias; error = rojo. */
  tipo: 'exito' | 'aviso' | 'error';
  textos: string[];
  /** Advertencias del back (al volver a LEGADO con franjas que rompen sus reglas). */
  advertencias?: string[];
  /** Con MARGENES alguna ruta no se grabó: se ofrece «Reintentar aplicar». */
  reintentar?: boolean;
}

/**
 * Lo que muestra la sección tras un PUT que respondió bien: el mensaje del
 * back, sus advertencias y, si con MARGENES alguna ruta no se grabó,
 * «Reintentar aplicar» (con LEGADO el back no regraba al reenviar).
 */
export function resultadoGuardado(
  r: GuardarMargenesVesResponse | null | undefined,
  motorAntes?: MotorTasasVes | null,
): ResultadoMargenes {
  const pendientes = rutasSinAplicar(r, motorAntes);
  const lista: unknown[] = Array.isArray(r?.advertencias) ? r?.advertencias ?? [] : [];
  const advertencias = lista
    .filter((a): a is string => typeof a === 'string' && !!a.trim())
    .map((a) => a.trim());
  const resultado: ResultadoMargenes = {
    tipo: pendientes.length || advertencias.length ? 'aviso' : 'exito',
    textos: textosGuardado(r, motorAntes),
  };
  if (advertencias.length) resultado.advertencias = advertencias;
  if (pendientes.length && motorDe(r?.motor) === 'MARGENES') resultado.reintentar = true;
  return resultado;
}

/**
 * El PUT falló o chocó, pero la relectura dice que lo guardado ya es lo que
 * se pidió. Con MARGENES no se sabe si las tasas se grabaron: se ofrece
 * «Reintentar aplicar».
 */
export function resultadoYaGuardado(motor: MotorTasasVes): ResultadoMargenes {
  return motor === 'MARGENES'
    ? { tipo: 'aviso', textos: [TEXTO_YA_GUARDADO, TEXTO_APLICAR_INCIERTO], reintentar: true }
    : { tipo: 'exito', textos: [TEXTO_YA_GUARDADO] };
}

/**
 * El PUT chocó con la versión: 409 (otro operador guardó antes) o 400
 * «Versión inválida: recarga los márgenes» (la que viajó no sirve). En los dos
 * no se guardó nada y se resuelve igual: releer lo guardado.
 */
export function esConflictoDeVersion(err: any): boolean {
  const s = statusDe(err);
  if (s === 409) return true;
  if (s !== 400) return false;
  const cuerpo = err?.originalError?.error ?? err?.error ?? null;
  const msg = Array.isArray(cuerpo?.message) ? cuerpo.message.join(' ') : cuerpo?.message;
  return typeof msg === 'string' && /versi[oó]n inv[aá]lida/i.test(msg);
}

/** El mensaje del 409 (o del 400 de versión) del PUT: el del back, o uno propio si no vino uno que sirva. */
export function mensajeConflictoMargenes(err: any): string {
  const cuerpo = err?.originalError?.error ?? err?.error ?? null;
  const msg = Array.isArray(cuerpo?.message) ? cuerpo.message.join(' ') : cuerpo?.message;
  return typeof msg === 'string' && msg.trim() && !esTextoDeProtocolo(msg) ? msg.trim() : TEXTO_CONFLICTO_MARGENES;
}

/** Status del error que deja el resultado del PUT en duda (pudo haberse guardado): sin conexión o 5xx (504 incluido). */
export function esErrorIncierto(err: any): boolean {
  const s = statusDe(err);
  return s === 0 || (typeof s === 'number' && s >= 500 && s <= 599);
}

// ───────────────────────── Tasas grabadas (panel) ─────────────────────────

/**
 * `actualizar-parametros`, `cambiar-operador` y `bancos-config` traen
 * `tasasGrabadas` (aditivo, 03/11/2026); `calcular-y-guardar` responde
 * `success: false` (`porSuccess`). Si alguna ruta no se grabó, el `message`
 * del back (dice cuál y por qué) para mostrarlo como aviso; null si se grabó
 * todo o el back no lo dice (back viejo: como siempre).
 */
export function avisoTasasNoGrabadas(r: any, porSuccess = false): string | null {
  const g = r?.tasasGrabadas;
  const noGrabadas = RUTAS_VES.filter((x) => g && typeof g === 'object' && g[x.ruta] === false).map((x) => x.etiqueta);
  if (!noGrabadas.length && !(porSuccess && r?.success === false)) return null;
  const msg = typeof r?.message === 'string' && r.message.trim() && !esTextoDeProtocolo(r.message) ? r.message.trim() : null;
  if (msg) return msg;
  return noGrabadas.length
    ? `Se guardó, pero las tasas de ${noGrabadas.join(' y ')} no se grabaron.`
    : 'Las tasas no se grabaron.';
}

// ───────────────────────── Lectura del GET ─────────────────────────

/**
 * Lectura defensiva de GET rates/margenes-ves. Sin un motor conocido (back
 * viejo, cuerpo vacío) devuelve null: ni las píldoras ni el modal pintan
 * nada a medias.
 */
export function normalizarMargenesVes(r: any): MargenesVesResponse | null {
  const motor = motorDe(r?.motor);
  if (!motor) return null;

  const rango = (x: any): RangoMargenVes | null => {
    const minimo = numOrNull(x?.minimo);
    const maximo = numOrNull(x?.maximo);
    return minimo != null && maximo != null ? { minimo, maximo } : null;
  };
  const spot = (x: any): SpotCruzadoVes | null => {
    const vesClp = numOrNull(x?.vesClp);
    const clpVes = numOrNull(x?.clpVes);
    return vesClp != null && clpVes != null && vesClp > 0 && clpVes > 0 ? { vesClp, clpVes } : null;
  };
  const tramos = (xs: any): TramoMargenVes[] =>
    (Array.isArray(xs) ? xs : [])
      .filter((t: any) => t && numOrNull(t.desde) != null && numOrNull(t.hasta) != null)
      .map((t: any) => ({
        clave: String(t.clave ?? `${t.desde}-${t.hasta}`),
        letra: String(t.letra ?? ''),
        desde: Number(t.desde),
        hasta: Number(t.hasta),
        margenAplicado: numOrNull(t.margenAplicado),
        tasaRuta: numOrNull(t.tasaRuta),
        tasaClient: numOrNull(t.tasaClient),
      }));

  const margenes = { clpVes: rango(r?.margenes?.clpVes), vesClp: rango(r?.margenes?.vesClp) };
  const limite = numOrNull(r?.limiteMaximoPct);
  return {
    motor,
    margenes,
    completos: typeof r?.completos === 'boolean' ? r.completos : !!(margenes.clpVes && margenes.vesClp),
    limiteMaximoPct: limite != null && limite > 0 ? limite : LIMITE_MARGEN_PCT,
    operador: typeof r?.operador === 'string' && r.operador ? r.operador : null,
    usdClp: numOrNull(r?.usdClp),
    rateVesUsdt: numOrNull(r?.rateVesUsdt),
    spotCruzado: spot(r?.spotCruzado),
    tramos: { clpVes: tramos(r?.tramos?.clpVes), vesClp: tramos(r?.tramos?.vesClp) },
    // Opaca: se devuelve tal cual en el PUT. Sin ella (back viejo), null.
    version: typeof r?.version === 'string' && r.version ? r.version : null,
  };
}

// ───────────────────────── Formato ─────────────────────────

/** «0,60 %», «3,575 %», «2,3333 %»: de 2 a 4 decimales; «—» sin número. */
export function formatoMargen(x: number | null | undefined): string {
  const n = Number(x);
  if (x == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: DECIMALES_MARGEN })} %`;
}

/** Tasa o spot con 4 a 6 decimales (es-CL); «—» sin número. */
export function formatoTasaVes(x: number | null | undefined, min = 4, max = 6): string {
  const n = Number(x);
  if (x == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CL', { minimumFractionDigits: min, maximumFractionDigits: max });
}

/** «1 CLP = 0,9859 VES» (la tasa que se graba, 4 decimales). */
export function textoTasaCliente(x: number | null | undefined): string {
  return x == null || !Number.isFinite(Number(x)) ? '—' : `1 CLP = ${formatoTasaVes(x, 4, 4)} VES`;
}

/** La tasa en la unidad de la ruta: CLP-VES «1 VES = 1,014332 CLP»; VES-CLP «1 CLP = 1,021116 VES». */
export function textoTasaRuta(par: ParFranja, x: number | null | undefined): string {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return par === 'CLP-VES' ? `1 VES = ${formatoTasaVes(x)} CLP` : `1 CLP = ${formatoTasaVes(x)} VES`;
}

/** «14:05:09» (hora local) de una lectura; null sin fecha válida. */
export function horaLectura(d: Date | null | undefined): string | null {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${dos(d.getHours())}:${dos(d.getMinutes())}:${dos(d.getSeconds())}`;
}

/** «15.000 – 99.999». */
export function rangoClp(desde: number | null | undefined, hasta: number | null | undefined): string {
  const f = (v: number | null | undefined) =>
    v == null || !Number.isFinite(Number(v)) ? '—' : Math.round(Number(v)).toLocaleString('es-CL');
  return `${f(desde)} – ${f(hasta)}`;
}
